/**
 * Investors API Module
 *
 * This module provides functions for interacting with the investor-related endpoints
 * of the WealthBee Enterprise API.
 */
import axios from "axios";
import { Investor } from "../types/Investor";
import getServiceEndpoint from "./ServiceEndpoint";
import { buildClientHeader } from "./buildHeader";

/**
 * Retrieves all investors from the API.
 *
 * @returns A promise that resolves to an array of Investor objects.
 * If the request fails, returns an empty array.
 */
export const getInvestors = async (): Promise<Investor[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/investors`,
      buildClientHeader()
    );
    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve investors", response.statusText);
      return [];
    }
    return response.data ?? [];
  } catch (error) {
    console.error("Error getting investors:", error);
  }

  return [];
};

/**
 * Retrieves a specific investor by their ID.
 *
 * @param id - The unique identifier of the investor to retrieve.
 * @returns A promise that resolves to an Investor object.
 * @throws Will throw an error if the API request fails.
 */
export const getInvestorById = async (id: number): Promise<Investor> => {
  const response = await axios.get(
    `${getServiceEndpoint()}/investors/${id}`,
    buildClientHeader()
  );
  return response.data;
};
